/**
 * Diagnose-Hilfe wenn Offline-Login fehlschlägt (localStorage / IndexedDB).
 */

import { kvGet } from '../infrastructure/storage/kvAccess';
import {
  extractBusinessesFromOfflineUsers,
  findBusinessForLogin,
  mergeOfflineBusinessRecords,
  parseOfflineBusinessList,
  readAuthListFromNativeLocalStorage
} from './authCredentialRead';

function normalizeLogin(value) {
  return String(value ?? '').trim().toLowerCase();
}

function findByLogin(list, login) {
  const needle = normalizeLogin(login);
  if (!needle) return null;
  return (
    list.find((b) => {
      const u = normalizeLogin(b?.username);
      const e = normalizeLogin(b?.email);
      return u === needle || e === needle;
    }) || null
  );
}

function readLsRaw(key) {
  if (typeof window === 'undefined' || !window.localStorage) {
    return { raw: null, bytes: 0, error: 'localStorage unavailable' };
  }
  try {
    const raw = window.localStorage.getItem(key);
    return { raw, bytes: raw ? raw.length : 0, error: null };
  } catch (e) {
    return { raw: null, bytes: 0, error: e?.message || String(e) };
  }
}

function flagPassword(row) {
  return !!(row?.password != null && String(row.password).length > 0);
}

/**
 * @param {string} username
 * @param {{ success?: boolean, error?: string, diagnostic?: object }|null} loginResult
 */
export function buildOfflineLoginDebug(username, loginResult = null) {
  const enteredLogin = normalizeLogin(username);
  const lsBiz = readLsRaw('offline_businesses');
  const lsUsers = readLsRaw('offline_users');
  const lsBusinesses = parseOfflineBusinessList(lsBiz.raw);
  const lsUsersList = parseOfflineBusinessList(lsUsers.raw);
  const kvBusinesses = parseOfflineBusinessList(kvGet('offline_businesses'));
  const kvUsers = parseOfflineBusinessList(kvGet('offline_users'));
  const businessesFromUsers = extractBusinessesFromOfflineUsers([
    ...lsUsersList,
    ...kvUsers
  ]);

  const merged = mergeOfflineBusinessRecords(
    lsBusinesses,
    businessesFromUsers,
    kvBusinesses
  );

  const account = findBusinessForLogin(username, merged);
  const matchLs = findByLogin(lsBusinesses, enteredLogin);
  const matchKv = findByLogin(kvBusinesses, enteredLogin);
  const matchFromUsers = findByLogin(businessesFromUsers, enteredLogin);
  const matchFromUsersById =
    account && businessesFromUsers.find((b) => String(b?.id) === String(account?.id));

  const mergedCount = merged.length;

  const hasStoredPassword = loginResult?.diagnostic?.hasStoredPassword;
  const passwordMissingEverywhere =
    account != null &&
    !flagPassword(matchLs) &&
    !flagPassword(matchKv) &&
    !flagPassword(matchFromUsers) &&
    !flagPassword(account);

  return {
    timestamp: new Date().toISOString(),
    enteredLogin: enteredLogin || '(empty)',
    isOnline: typeof navigator !== 'undefined' ? navigator.onLine : null,
    localBusinessCount: mergedCount,
    accountFound: account != null,
    businessId: account?.id || null,
    username: account?.username || null,
    email: account?.email || null,
    hasPasswordMerged: flagPassword(account),
    hasPasswordLocalStorage: flagPassword(matchLs),
    hasPasswordIndexedDbCache: flagPassword(matchKv),
    hasPasswordInOfflineUsers: flagPassword(matchFromUsersById || matchFromUsers),
    passwordOnlyInOfflineUsers:
      !flagPassword(matchLs) &&
      !flagPassword(matchKv) &&
      flagPassword(matchFromUsersById || matchFromUsers),
    hasManagerCode: !!(account?.manager_code && String(account.manager_code).trim()),
    lsOfflineBusinessesBytes: lsBiz.bytes,
    lsOfflineUsersBytes: lsUsers.bytes,
    lsBusinessCount: lsBusinesses.length,
    kvBusinessCount: kvBusinesses.length,
    lsReadError: lsBiz.error,
    loginError: loginResult?.error || null,
    passwordVerifyFailed: hasStoredPassword === false || passwordMissingEverywhere,
    suggestedAction: !account
      ? 'no_account'
      : passwordMissingEverywhere || hasStoredPassword === false
        ? 'use_forgot_password'
        : 'wrong_password_or_forgot'
  };
}

/**
 * Console dump is disabled — Login UI already shows formatLoginDebugLines.
 * Kept as a no-op so call sites stay stable (ADR-0002: no raw localStorage here).
 */
export function logOfflineLoginDebug(_debug) {
  void _debug;
}

/**
 * @param {ReturnType<typeof buildOfflineLoginDebug>} debug
 * @param {(key: string) => string} t — authPage.* translator
 */
export function formatLoginDebugLines(debug, t) {
  if (!debug) return [];
  const yes = t('debugYes');
  const no = t('debugNo');

  const lines = [
    `${t('debugEnteredLogin')}: ${debug.enteredLogin}`,
    `${t('debugOnline')}: ${debug.isOnline ? yes : no}`,
    `${t('debugLocalAccounts')}: ${debug.localBusinessCount}`,
    `${t('debugAccountFound')}: ${debug.accountFound ? yes : no}`
  ];

  if (debug.accountFound) {
    lines.push(`${t('debugBusinessId')}: ${debug.businessId || '—'}`);
    lines.push(`${t('debugUsername')}: ${debug.username || '—'}`);
    lines.push(`${t('debugEmail')}: ${debug.email || '—'}`);
    lines.push(`${t('debugPasswordInLocalStorage')}: ${debug.hasPasswordLocalStorage ? yes : no}`);
    lines.push(`${t('debugPasswordInIndexedDb')}: ${debug.hasPasswordIndexedDbCache ? yes : no}`);
    lines.push(`${t('debugPasswordInOfflineUsers')}: ${debug.hasPasswordInOfflineUsers ? yes : no}`);
    lines.push(`${t('debugManagerCodePresent')}: ${debug.hasManagerCode ? yes : no}`);
  }

  lines.push(`${t('debugLsBusinessBytes')}: ${debug.lsOfflineBusinessesBytes}`);
  lines.push(`${t('debugLsBusinessCount')}: ${debug.lsBusinessCount}`);

  if (debug.lsReadError) {
    lines.push(`${t('debugLsError')}: ${debug.lsReadError}`);
  }

  if (debug.suggestedAction === 'use_forgot_password') {
    lines.push(t('debugHintForgotPassword'));
  } else if (debug.suggestedAction === 'wrong_password_or_forgot') {
    lines.push(t('debugHintWrongPassword'));
  } else if (debug.suggestedAction === 'no_account') {
    lines.push(t('debugHintNoAccount'));
  }

  return lines;
}
